import { push } from 'connected-react-router'
import { ChainId } from '@ocean-club/schemas'
import { getChainId } from '@ocean-club/dapps/dist/modules/wallet/selectors'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import {
  ACCEPT_BID_REQUEST,
  acceptBidFailure,
  AcceptBidRequestAction,
  acceptBidSuccess,
  CANCEL_BID_REQUEST,
  cancelBidFailure,
  CancelBidRequestAction,
  cancelBidSuccess,
  FETCH_BIDS_BY_ADDRESS_REQUEST,
  FETCH_BIDS_BY_NFT_REQUEST,
  fetchBidsByAddressFailure,
  FetchBidsByAddressRequestAction,
  fetchBidsByAddressSuccess,
  fetchBidsByNFTFailure,
  FetchBidsByNFTRequestAction,
  fetchBidsByNFTSuccess,
  PLACE_BID_REQUEST,
  placeBidFailure,
  PlaceBidRequestAction,
  placeBidSuccess
} from './actions'
import { getWallet } from '../wallet/selectors'
import { locations } from '../routing/locations'
import { VendorFactory } from '../vendor/VendorFactory'
import { getContract } from '../contract/utils'
import { VendorName } from '../vendor/types'
import { Bid } from './types'

export function* bidSaga() {
  yield takeEvery(PLACE_BID_REQUEST, handlePlaceBidRequest)
  yield takeEvery(ACCEPT_BID_REQUEST, handleAcceptBidRequest)
  yield takeEvery(CANCEL_BID_REQUEST, handleCancelBidRequest)
  yield takeEvery(
    FETCH_BIDS_BY_ADDRESS_REQUEST,
    handleFetchBidsByAddressRequest
  )
  yield takeEvery(FETCH_BIDS_BY_NFT_REQUEST, handleFetchBidsByNFTRequest)
}

function* handlePlaceBidRequest(action: PlaceBidRequestAction) {
  const { nft, price } = action.payload
  try {
    const { bidService } = VendorFactory.build(nft.vendor)

    const wallet: ReturnType<typeof getWallet> = yield select(getWallet)
    const txHash: string = yield call(() =>
      bidService!.place(wallet, nft, price)
    )
    const chainId: ChainId = yield select(getChainId)
    yield put(
      placeBidSuccess(
        nft,
        price,
        chainId,
        txHash,
        wallet!.address
      )
    )
    yield put(push(locations.activity()))
  } catch (error) {
    yield put(placeBidFailure(nft, price, error))
  }
}

function* handleAcceptBidRequest(action: AcceptBidRequestAction) {
  const { bid } = action.payload
  try {
    const { bidService } = VendorFactory.build(VendorName.OCEAN)

    const wallet: ReturnType<typeof getWallet> = yield select(getWallet)
    const txHash: string = yield call(() => bidService!.accept(wallet, bid))

    const chainId: ChainId = yield select(getChainId)
    yield put(acceptBidSuccess(bid, chainId, txHash))
    yield put(push(locations.activity()))
  } catch (error) {
    yield put(acceptBidFailure(bid, error.message))
  }
}

function* handleCancelBidRequest(action: CancelBidRequestAction) {
  const { bid } = action.payload
  try {
    const contract = getContract({ address: bid.contractAddress })
    if (!contract.vendor) {
      throw new Error(
        `Couldn't find a valid vendor for contract ${contract.address}`
      )
    }
    const { bidService } = VendorFactory.build(contract.vendor)

    const wallet: ReturnType<typeof getWallet> = yield select(getWallet)
    const chainId: ChainId = yield select(getChainId)
    const txHash: string = yield call(() => bidService!.cancel(wallet, bid))

    yield put(cancelBidSuccess(bid, chainId, txHash))
    yield put(push(locations.activity()))
  } catch (error) {
    yield put(cancelBidFailure(bid, error.message))
  }
}

function* handleFetchBidsByAddressRequest(
  action: FetchBidsByAddressRequestAction
) {
  const { address } = action.payload
  try {
    let sellerBids: Bid[] = []
    let bidderBids: Bid[] = []

    for (const vendorName of Object.values(VendorName)) {
      const { bidService } = VendorFactory.build(vendorName)
      if (bidService === undefined) {
        continue
      }

      const bids: [Bid[], Bid[]] = yield call(() =>
        Promise.all([
          bidService.fetchBySeller(address),
          bidService.fetchByBidder(address)
        ])
      )
      sellerBids = sellerBids.concat(bids[0])
      bidderBids = bidderBids.concat(bids[1])
    }

    yield put(fetchBidsByAddressSuccess(address, sellerBids, bidderBids))
  } catch (error) {
    yield put(fetchBidsByAddressFailure(address, error.message))
  }
}

function* handleFetchBidsByNFTRequest(action: FetchBidsByNFTRequestAction) {
  const { nft } = action.payload
  try {
    const { bidService } = VendorFactory.build(nft.vendor)

    const bids: Bid[] = yield call(() => bidService!.fetchByNFT(nft))

    yield put(fetchBidsByNFTSuccess(nft, bids))
  } catch (error) {
    yield put(fetchBidsByNFTFailure(nft, error.message))
  }
}
